import React from 'react'
import { log } from '../utils/logger'
import {
    Table,
    Group,
    Text,
    Badge,
    Avatar,
    Pagination,
    Stack,
    Center,
    Paper,
} from '@mantine/core'
import { IconTrendingDown } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import type { PriceDropGamePlatform, PaginatedApiResponse } from '../types'
import { useI18n } from '@/hooks/useI18n'
import { getPlatformColor } from '../utils/platform-colors'
import { useDarkMode } from '@/hooks/useDarkMode'

interface PriceDropsTableProps {
    data: PaginatedApiResponse<PriceDropGamePlatform> | null
    loading?: boolean
    onPageChange?: (page: number) => void
    // Optional platform filtering - when provided, only show items for this platform
    platformSlug?: string
}

export const PriceDropsTable: React.FC<PriceDropsTableProps> = ({
    data,
    loading = false,
    onPageChange,
    platformSlug,
}) => {
    const { t, currency } = useI18n()
    const { colorScheme } = useDarkMode()
    const priceDrops = data?.data || []
    const pagination = data?.pagination || null

    // Debug logging
    log('PriceDropsTable render:', {
        dataLength: priceDrops.length,
        currentPage: pagination?.page,
        totalPages: pagination?.totalPages,
        loading,
        platformSlug,
        samplePlatforms: priceDrops.slice(0, 3).map(item => item.platform.slug)
    })

    if (loading) {
        return (
            <Stack gap="md">
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>{t('home.priceDrops.gameTitle')}</Table.Th>
                            <Table.Th ta="right">{t('home.priceDrops.priceChange')}</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {Array.from({ length: 5 }).map((_, index) => (
                            <Table.Tr key={index}>
                                <Table.Td>
                                    <Group gap="sm">
                                        <Avatar size={40} radius="sm" />
                                        <div>
                                            <Text fw={500} size="sm">{t('common.loading')}</Text>
                                            <Badge variant="light" size="xs">{t('common.loading')}</Badge>
                                        </div>
                                    </Group>
                                </Table.Td>
                                <Table.Td ta="right">
                                    <Stack gap={2} align="flex-end">
                                        <Group gap={8} justify="flex-end">
                                            <Text c="dimmed" td="line-through" size="sm">€--</Text>
                                            <Text fw={600} size="sm">€--</Text>
                                        </Group>
                                        <Badge color="green" variant="light" size="xs">
                                            <Group gap={4}>
                                                <IconTrendingDown size={10} />
                                                --%
                                            </Group>
                                        </Badge>
                                    </Stack>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Stack>
        )
    }

    const formatPrice = (amount: number, currencyCode: string = currency) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
        }).format(amount)
    }

    return (
        <div style={{ width: '100%' }}>
            <Stack gap="md" w="100%">
                {priceDrops.length === 0 ? (
                    <Text ta="center" c="dimmed" py="xl">
                        {platformSlug
                            ? `No price drops found for this platform`
                            : t('home.priceDrops.noDrops')
                        }
                    </Text>
                ) : (
                    <Stack gap={0}>
                        {priceDrops.map((gamePlatform) => {
                            const imageUrl = gamePlatform.imageUrl
                            const lowestPrice = gamePlatform.lowestPrice

                            // Skip if no lowest price or no price drop
                            if (!lowestPrice || Number(lowestPrice.percentageChange) >= 0) {
                                return null
                            }

                            // Convert string values to numbers for calculations
                            const currentPrice = Number(lowestPrice.amount)
                            const priceDrop = Number(lowestPrice.priceDrop)
                            const percentageChange = Number(lowestPrice.percentageChange)

                            // Calculate old price from current price and drop amount
                            const oldPrice = currentPrice + priceDrop
                            const dropPercentage = Math.abs(percentageChange)

                            return (
                                <Paper
                                    key={`${gamePlatform.gameId}-${gamePlatform.platformId}`}
                                    p="xs"
                                    radius="xl"
                                    component={Link}
                                    to={`/games/${gamePlatform.game.slug}/platforms/${gamePlatform.platform.slug}`}
                                    style={{
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        cursor: 'pointer',
                                        transition: 'transform 0.1s ease'
                                    }}
                                    className="hover:transform hover:scale-[1.02]"
                                >
                                    <Group justify="space-between" align="center">
                                        <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                                            <Avatar
                                                src={imageUrl}
                                                alt={gamePlatform.game?.title}
                                                size={50}
                                                radius="sm"
                                            />
                                            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                                                <Text
                                                    fw={400}
                                                    size="sm"
                                                    lineClamp={2}
                                                    c={colorScheme === 'dark' ? 'customYellow' : 'var(--mantine-color-text)'}
                                                >
                                                    {gamePlatform.game.title}
                                                </Text>
                                                <Badge
                                                    variant="light"
                                                    color={getPlatformColor(gamePlatform.platform)}
                                                    size="xs"
                                                    w="fit-content"
                                                >
                                                    {gamePlatform.platform.name}
                                                </Badge>
                                            </Stack>
                                        </Group>
                                        <Stack gap={4} align="flex-end" style={{ flexShrink: 0 }}>
                                            <Group gap={6}>
                                                <Text
                                                    c="dimmed"
                                                    td="line-through"
                                                    size="xs"
                                                >
                                                    {formatPrice(oldPrice, lowestPrice.currency)}
                                                </Text>
                                                <Text fw={600} size="md" c="customGreen">
                                                    {formatPrice(currentPrice, lowestPrice.currency)}
                                                </Text>
                                            </Group>
                                            <Badge color="customGreen" variant="light" size="xs">
                                                <Group gap={3}>
                                                    <IconTrendingDown size={10} />
                                                    {dropPercentage.toFixed(0)}% {t('home.priceDrops.off')}
                                                </Group>
                                            </Badge>
                                        </Stack>
                                    </Group>
                                </Paper>
                            )
                        })}
                    </Stack>
                )}

                {pagination && pagination.totalPages > 1 && (
                    <Center>
                        <Pagination
                            value={pagination.page}
                            total={pagination.totalPages}
                            onChange={onPageChange}
                            size="sm"
                            withEdges
                        />
                    </Center>
                )}
            </Stack>
        </div>
    )
} 